/**
 * Turning a due date into a Mum Hub stage, on the web (MAR-219).
 *
 * A port of `mobile/src/lib/mum-hub/stage.ts`, which is where this arithmetic
 * was worked out (MAR-154). It is copied rather than shared because the two
 * apps do not share a bundle: mobile resolves `@cch/shared` through Metro and
 * this file would have to move into `packages/cch-shared` to be imported by
 * both, which is a change to a package both apps ship and not something to do
 * from inside a web feature. `client/src/lib/__tests__/mum-hub-stage.test.ts`
 * pins the same boundaries the mobile suite pins, so the two cannot drift
 * silently on the only thing that matters here, which is where a stage begins.
 *
 * ALL of Mum Hub's date arithmetic lives here, and it is pure: every function
 * that needs to know what day it is takes `now` as an argument. Nothing here
 * reads the clock, so every boundary is reachable from a test without faking
 * time, and no component ever does date maths of its own.
 *
 * WHY A DUE DATE AND NOT A STAGE PICKER. A picker goes stale silently: she
 * chooses second trimester and is still being shown it three months later. A
 * date is answered once and the stage follows on its own.
 *
 * ## The arithmetic, stated plainly
 *
 * Gestational age is counted in COMPLETED weeks and days, written `n w d`. So
 * `n w 0d` is gestational day n*7: 28w0d is day 196, and a due date, which is
 * 40w0d, is day 280. Gestational day = 280 minus the days until she is due,
 * and the clinical trimester boundaries fall like this:
 *
 * | Stage             | Gestational age    | Gestational days | Days until due |
 * |-------------------|--------------------|------------------|----------------|
 * | First trimester   | up to 13w6d        | 0 to 97          | 183 and more   |
 * | Second trimester  | 14w0d to 27w6d     | 98 to 195        | 85 to 182      |
 * | Third trimester   | 28w0d to 39w6d     | 196 to 279       | 1 to 84        |
 * | New mum           | 40w0d and after    | 280 and more     | 0 and below    |
 *
 * THE MISTAKE THIS ARITHMETIC HAS ALREADY MADE ONCE, so that nobody makes it
 * again: an earlier version of the mobile file stated "week n covers days
 * (n-1)*7 through n*7-1" and set the boundaries to days 91 and 189. That rule
 * contradicts its own anchor, because under it day 280 falls in week 41 rather
 * than being 40w0d, and it put both boundaries a week early. A customer at
 * 27w3d was told she was in her third trimester. If you are tempted to change
 * these constants, first check your rule against the due date: whatever
 * convention you use must put day 280 at 40w0d.
 *
 * ## The two boundary decisions worth stating out loud
 *
 * 1. The due date itself counts as new mum. On the due date and every day
 *    after it she is in the new mum stage. We do not know whether the baby has
 *    arrived, and holding her in the third trimester past her own due date is
 *    the version that reads badly.
 * 2. A birth date is stored in the same field. Someone who has already given
 *    birth gives us that date instead, which is in the past, so `daysUntilDue`
 *    is negative and she lands in new mum immediately. There is no separate
 *    "is this a birth date" flag and there does not need to be.
 *
 * ## After the post-partum window
 *
 * New mum lasts {@link POST_PARTUM_WINDOW_DAYS} days counted from the date
 * itself, so the last day inside it is day 90. From day 91 the derivation
 * returns `{ kind: 'window-ended' }` rather than pinning her to new
 * mum for ever. Deliberately NOT an error and NOT null: the date on file is
 * still valid, it has simply aged out of the stages Mum Hub covers, and the
 * page's job is then to ask whether there is a newer date. Callers must handle
 * it; the union type makes that non-optional.
 *
 * ## Privacy
 *
 * The date is health-adjacent personal data. Nothing here logs, and nothing
 * here builds an error message containing the date. `parseIsoDate` returns
 * null on a malformed value rather than throwing with the value attached.
 */

import type { MumHubStageId } from "./stages";

export type { MumHubStageId };

/** Days from the last period to the due date. A due date is day 280. */
export const GESTATION_DAYS = 280;

/** First gestational day of the second trimester: 14w0d, which is 14*7. */
export const SECOND_TRIMESTER_START_DAY = 98;

/** First gestational day of the third trimester: 28w0d, which is 28*7. */
export const THIRD_TRIMESTER_START_DAY = 196;

/**
 * How long the new mum stage lasts, counted in days from the due or birth
 * date. New mum means the first three months after the birth, which is
 * thirteen weeks: day 0 is the date itself, so the window's last day is day
 * 90. Mirrors the Worker's `POST_PARTUM_WINDOW_DAYS`; keep the two in step.
 */
export const POST_PARTUM_WINDOW_DAYS = 91;

/**
 * How far ahead a due date may be, in days, inclusive. Mirrors the Worker's
 * `DUE_DATE_MAX_DAYS_AHEAD` so the form cannot offer a date the API rejects.
 */
export const DUE_DATE_MAX_DAYS_AHEAD = 308;

/**
 * How far behind a due or birth date may be, in days, inclusive. Mirrors the
 * Worker's `DUE_DATE_MAX_DAYS_BEHIND`. One day short of the post-partum
 * window, so every date the API accepts still lands her in a stage.
 */
export const DUE_DATE_MAX_DAYS_BEHIND = 90;

/**
 * The outcome of deriving a stage. A union rather than a nullable stage so
 * that "her window has ended" cannot be handled by accident as "she has no
 * date on file". Those two want completely different screens.
 */
export type MumHubStageResult =
  | { kind: "stage"; stage: MumHubStageId }
  | { kind: "window-ended" };

const MS_PER_DAY = 86_400_000;

/** Strict `YYYY-MM-DD`. Rejects anything that is not a real calendar day. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Days since the epoch for a calendar date, ignoring time of day entirely.
 * Whole days only, so no timezone offset or daylight-saving shift can move a
 * boundary by one.
 */
function dayNumber(year: number, month: number, day: number): number {
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

/**
 * `YYYY-MM-DD` to a day number, or null if it is not a real date. Round-trips
 * through UTC so `2026-02-30` is rejected rather than rolling into March.
 *
 * Never throws and never echoes the input, because the input is a due date.
 */
export function parseIsoDate(value: string): number | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }
  return utc.getTime() / MS_PER_DAY;
}

/** A `Date` to `YYYY-MM-DD` in the LOCAL calendar, which is the one she is in. */
export function toIsoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * `now` as a day number in her LOCAL calendar. Deliberately not UTC: at 23:00
 * in London in July, UTC is already tomorrow, and a customer one day either
 * side of a trimester boundary would see the wrong stage for an hour.
 */
export function localDayNumber(now: Date): number {
  return dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/**
 * Whole days from `now` to the due date. Positive means still to come, zero
 * means today, negative means it has passed.
 */
export function daysUntilDue(dueDate: string, now: Date): number | null {
  const due = parseIsoDate(dueDate);
  if (due === null) return null;
  return due - localDayNumber(now);
}

/** First day of the second trimester, expressed as days until due. */
const SECOND_TRIMESTER_MAX_DAYS_UNTIL_DUE =
  GESTATION_DAYS - SECOND_TRIMESTER_START_DAY;

/** First day of the third trimester, expressed as days until due. */
const THIRD_TRIMESTER_MAX_DAYS_UNTIL_DUE =
  GESTATION_DAYS - THIRD_TRIMESTER_START_DAY;

/**
 * The stage a customer is in on `now`, given the date on file.
 *
 * Returns null only when `dueDate` is not a real `YYYY-MM-DD` date, which is a
 * programming error rather than a state a customer can reach: the API returns
 * either a valid date or null, and null means she has not answered, which is
 * the caller's business and not this function's.
 */
export function deriveMumHubStage(
  dueDate: string,
  now: Date,
): MumHubStageResult | null {
  const days = daysUntilDue(dueDate, now);
  if (days === null) return null;

  if (days > SECOND_TRIMESTER_MAX_DAYS_UNTIL_DUE) {
    return { kind: "stage", stage: "first-trimester" };
  }
  if (days > THIRD_TRIMESTER_MAX_DAYS_UNTIL_DUE) {
    return { kind: "stage", stage: "second-trimester" };
  }
  if (days > 0) {
    return { kind: "stage", stage: "third-trimester" };
  }
  // On the date itself and after it. The window is counted forward from the
  // date, so `-days` is how many days have passed and day 0 is inside it.
  if (-days < POST_PARTUM_WINDOW_DAYS) {
    return { kind: "stage", stage: "new-mum" };
  }
  return { kind: "window-ended" };
}

/**
 * Whether a date is one the API will accept, mirroring the Worker's bounds so
 * the form never offers a value that comes back a 400. Both ends inclusive.
 */
export function isDueDateWithinBounds(dueDate: string, now: Date): boolean {
  const days = daysUntilDue(dueDate, now);
  if (days === null) return false;
  return days <= DUE_DATE_MAX_DAYS_AHEAD && -days <= DUE_DATE_MAX_DAYS_BEHIND;
}

/**
 * Month-only precision means the 1st of that month, and the Worker rejects any
 * other day with 400. Encoded here so the form cannot compose an invalid pair.
 */
export function isPrecisionConsistent(
  dueDate: string,
  precision: "day" | "month",
): boolean {
  if (precision === "day") return parseIsoDate(dueDate) !== null;
  const match = ISO_DATE.exec(dueDate);
  return match !== null && match[3] === "01" && parseIsoDate(dueDate) !== null;
}

/**
 * How far through the stage she is, for the counter beside the chapter title.
 *
 * Three different units because one unit reads wrong somewhere. Gestational
 * weeks are what a pregnant woman is counting and what every midwife says.
 * After the date, weeks are what she is counting for about two months and
 * months after that, because "week 34" means nothing to anyone.
 *
 * Null rather than a zero when there is nothing honest to print: an invalid
 * date, a date so far ahead that the gestational day is negative, or a window
 * that has already ended.
 */
export type MumHubStageProgress =
  | { kind: "gestation-week"; week: number }
  | { kind: "since-week"; week: number }
  | { kind: "since-month"; month: number };

/** Where weeks stop reading naturally and months take over. */
export const SINCE_WEEKS_LIMIT_DAYS = 56;

/** 365.25 / 12. Months are not equal, and a counter must not drift. */
export const AVERAGE_DAYS_PER_MONTH = 30.4375;

export function deriveStageProgress(
  dueDate: string,
  now: Date,
): MumHubStageProgress | null {
  const days = daysUntilDue(dueDate, now);
  if (days === null) return null;
  if (days > 0) {
    const gestationalDay = GESTATION_DAYS - days;
    if (gestationalDay < 0) return null;
    return { kind: "gestation-week", week: Math.floor(gestationalDay / 7) };
  }
  const since = -days;
  if (since >= POST_PARTUM_WINDOW_DAYS) return null;
  if (since < SINCE_WEEKS_LIMIT_DAYS) {
    return { kind: "since-week", week: Math.floor(since / 7) + 1 };
  }
  return {
    kind: "since-month",
    month: Math.floor(since / AVERAGE_DAYS_PER_MONTH) + 1,
  };
}
